import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Buildings, ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, absoluteUrl } from "@/api/client";
import { toast } from "sonner";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NDM2MzgxMHww&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@developeros.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ name: "Gray Owl", tagline: "Workspace", logo_url: "", login_image_url: "" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/workspace/branding").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const heroSrc = brand.login_image_url ? absoluteUrl(brand.login_image_url) : DEFAULT_HERO;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10" data-testid="login-brand">
            {brand.logo_url ? (
              <img src={absoluteUrl(brand.logo_url)} alt="logo" data-testid="login-logo" className="w-10 h-10 rounded-md object-contain bg-neutral-900 p-1" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-neutral-900 flex items-center justify-center">
                <Buildings size={22} weight="fill" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg text-neutral-900 leading-none">{brand.name || "Gray Owl"}</div>
              <div className="text-[11px] text-neutral-500 mt-1 uppercase tracking-wider">{brand.tagline || "Workspace"}</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold text-neutral-900 mb-2">Welcome back</h1>
          <p className="text-sm text-neutral-500 mb-8">Sign in to your {brand.name || "Gray Owl"} workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="dos-input mt-1.5" required />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="dos-input mt-1.5" required />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading} className="dos-btn dos-btn-primary w-full mt-2">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={14} /></>)}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-neutral-200" />
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">or</div>
            <div className="flex-1 h-px bg-neutral-200" />
          </div>

          <button
            data-testid="google-signin-btn"
            type="button"
            onClick={startGoogleLogin}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-medium text-neutral-800 transition-colors duration-200"
          >
            <GoogleLogo size={16} weight="bold" />
            Sign in with Google
          </button>

          <div className="mt-8 p-3 rounded-md bg-neutral-100 border border-neutral-200 text-xs text-neutral-600">
            <div className="font-semibold text-neutral-700 mb-1">Demo credentials</div>
            <div>admin@developeros.com · admin123</div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" data-testid="login-hero">
        <img src={heroSrc} alt="Architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-900/40" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="font-display text-2xl font-semibold mb-2 leading-tight">A simple finance &amp; accounting workspace, built for owners.</div>
          <div className="text-sm text-white/80">One workspace for sales, expenses, invoices and multi-company accounts.</div>
        </div>
      </div>
    </div>
  );
}
